import { useFetchWisdomFollowUps } from '@modules/common/hooks';
import {
  useGenerateWisdomResponse,
  useGetPromptPosition,
} from '@modules/oracle/hooks';
import { useOracleSearchStore } from '@modules/oracle/store/oracleSearchStore';
import { trackWisdomEvents } from '@modules/oracle/utils';
import { ArrowForwardRounded } from '@mui/icons-material';
import { Divider, Stack, Typography } from '@mui/material';

interface SuggestedFollowUpQuestionsProps {
  sessionID: string | undefined;
  prompt: string;
  answerMarkdown: string;
}

export const WisdomFollowUpQuestions = ({
  sessionID,
  prompt,
  answerMarkdown,
}: SuggestedFollowUpQuestionsProps) => {
  const currentPromptID = useOracleSearchStore(
    ({ data }) => data.currentPromptID,
  );
  const threadPosition = useGetPromptPosition(currentPromptID);

  const [generateWisdomResponse, { isLoading: isWisdomResponseGenerated }] =
    useGenerateWisdomResponse();

  const postFollowupQuestion = async (suggestedQuestion: string) => {
    await generateWisdomResponse({
      question: suggestedQuestion,
      onGenerate: () => {
        trackWisdomEvents({
          eventName: 'Wisdom - Related Queries Clicked',
          parentQuestion: prompt,
          clickedQuery: suggestedQuestion,
          sessionID: sessionID,
          threadPosition,
        });
      },
    });
  };

  const {
    followups: wisdomFollowUps,
    loading: isWisdomFollowUpsLoading = false,
    error: fetchFollowupsError,
  } = useFetchWisdomFollowUps({
    prompt,
    responseMarkdown: answerMarkdown,
  });

  if (fetchFollowupsError) {
    return null;
  }

  const wisdomFollowUpTitle = (
    <Typography
      variant="subtitle2"
      sx={{
        animation: isWisdomFollowUpsLoading
          ? 'textAnimation 1s ease infinite'
          : 'none',
        background: 'linear-gradient(90deg, #d1d5db, #6b7280, #d1d5db)',
        backgroundSize: '200% 200%',
        backgroundPosition: 'left center',
        WebkitBackgroundClip: 'text',
        color: isWisdomFollowUpsLoading ? 'transparent' : 'text.secondary',
        display: 'inline-block',
        '@keyframes textAnimation': {
          '0%, 100%': {
            backgroundPosition: 'left center',
          },
          '50%': {
            backgroundPosition: 'right center',
          },
        },
      }}
    >
      {isWisdomFollowUpsLoading
        ? 'Loading Related Queries...'
        : 'Related Queries'}
    </Typography>
  );

  return (
    <Stack width={'100%'} spacing={1}>
      {wisdomFollowUpTitle}
      {!isWisdomFollowUpsLoading && wisdomFollowUps ? (
        <Stack spacing={1} sx={{ my: 2 }}>
          {wisdomFollowUps.map((wisdomFollowUp, index) => (
            <Stack key={wisdomFollowUp.followUpContent}>
              <Stack
                direction={'row'}
                px={1}
                spacing={0.5}
                justifyContent={'space-between'}
                sx={{
                  cursor: isWisdomResponseGenerated ? 'progress' : 'pointer',
                  '&:hover': {
                    color: 'primary.main',
                    '& .MuiSvgIcon-root': {
                      color: 'primary.main',
                    },
                  },
                }}
                onClick={() => {
                  postFollowupQuestion(wisdomFollowUp.followUpContent);
                }}
              >
                <Typography key={index} variant="body2" color="inherit">
                  {wisdomFollowUp.followUpContent}
                </Typography>
                <ArrowForwardRounded
                  sx={{
                    color: 'text.secondary',
                  }}
                />
              </Stack>
              {index < wisdomFollowUps.length - 1 && <Divider light />}
            </Stack>
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
};
